import { GoogleSpreadsheet } from 'google-spreadsheet';

// Google Sheets 서비스 클래스
class GoogleSheetsService {
  constructor() {
    this.doc = null;
    this.initialized = false;
  }

  // 스프레드시트 초기화
  async initialize(spreadsheetId, serviceAccountKey) {
    try {
      this.doc = new GoogleSpreadsheet(spreadsheetId);
      
      // 서비스 계정으로 인증
      await this.doc.useServiceAccountAuth({
        client_email: serviceAccountKey.client_email,
        private_key: serviceAccountKey.private_key,
      });

      await this.doc.loadInfo();
      this.initialized = true;
      

      return true;
    } catch (error) {
      console.error('Google Sheets 연결 실패:', error);
      return false;
    }
  }

  // 시트 생성 또는 가져오기
  async getOrCreateSheet(sheetTitle) {
    if (!this.initialized) {
      throw new Error('Google Sheets가 초기화되지 않았습니다.');
    }

    let sheet = this.doc.sheetsByTitle[sheetTitle];
    
    if (!sheet) {
      // 시트가 없으면 생성
      sheet = await this.doc.addSheet({
        title: sheetTitle,
        headerValues: [
          '과목코드',
          '과목명', 
          '학습단위',
          '학습제목',
          '토픽ID',
          '토픽명',
          '내용유형',
          '키워드',
          '요약내용',
          '상세설명',
          '청크수',
          '총길이',
          '수정일시'
        ]
      });

    }
    
    return sheet;
  }

  // 데이터 추가
  async addData(sheetTitle, data) {
    try {
      const sheet = await this.getOrCreateSheet(sheetTitle);
      
      const rows = data.map(item => ({
        '과목코드': item.subjectCode,
        '과목명': item.subjectName,
        '학습단위': item.lessonNum,
        '학습제목': item.lessonTitle,
        '토픽ID': item.topicId,
        '토픽명': item.topicTitle,
        '내용유형': item.contentType,
        '키워드': item.keywords,
        '요약내용': item.summary,
        '상세설명': item.details,
        '청크수': item.chunkCount,
        '총길이': item.totalLength,
        '수정일시': new Date().toISOString()
      }));

      await sheet.addRows(rows);

      
      return true;
    } catch (error) {
      console.error('데이터 추가 실패:', error);
      return false;
    }
  }

  // 모든 데이터 가져오기
  async getAllData(sheetTitle) {
    try {
      const sheet = await this.getOrCreateSheet(sheetTitle);
      const rows = await sheet.getRows();
      
      return rows.map(row => ({
        subjectCode: row.get('과목코드'),
        subjectName: row.get('과목명'),
        lessonNum: row.get('학습단위'),
        lessonTitle: row.get('학습제목'),
        topicId: row.get('토픽ID'),
        topicTitle: row.get('토픽명'),
        contentType: row.get('내용유형'),
        keywords: row.get('키워드'),
        summary: row.get('요약내용'),
        details: row.get('상세설명'),
        chunkCount: row.get('청크수'),
        totalLength: row.get('총길이'),
        updatedAt: row.get('수정일시')
      }));
    } catch (error) {
      console.error('데이터 가져오기 실패:', error);
      return [];
    }
  }

  // 특정 과목 데이터 가져오기
  async getSubjectData(subjectCode) {
    try {
      const allSheets = this.doc.sheetsByIndex;
      let subjectData = [];

      for (const sheet of allSheets) {
        if (sheet.title.includes(subjectCode) || sheet.title === '전체') {
          const rows = await sheet.getRows();
          const filteredRows = rows.filter(row => 
            row.get('과목코드') === subjectCode
          );
          
          subjectData = subjectData.concat(filteredRows.map(row => ({
            subjectCode: row.get('과목코드'),
            subjectName: row.get('과목명'),
            lessonNum: row.get('학습단위'),
            lessonTitle: row.get('학습제목'),
            topicId: row.get('토픽ID'),
            topicTitle: row.get('토픽명'),
            contentType: row.get('내용유형'),
            keywords: row.get('키워드'),
            summary: row.get('요약내용'),
            details: row.get('상세설명'),
            chunkCount: parseInt(row.get('청크수')) || 0,
            totalLength: parseInt(row.get('총길이')) || 0,
            updatedAt: row.get('수정일시')
          })));
        }
      }

      return subjectData;
    } catch (error) {
      console.error('과목별 데이터 가져오기 실패:', error);
      return [];
    }
  }

  // 데이터 업데이트
  async updateData(sheetTitle, rowIndex, updatedData) {
    try {
      const sheet = await this.getOrCreateSheet(sheetTitle);
      const rows = await sheet.getRows();
      
      if (rowIndex < rows.length) {
        const row = rows[rowIndex];
        
        Object.keys(updatedData).forEach(key => {
          if (key !== 'updatedAt') {
            row.set(key, updatedData[key]);
          }
        });
        
        row.set('수정일시', new Date().toISOString());
        await row.save();
        

        return true;
      }
      
      return false;
    } catch (error) {
      console.error('데이터 업데이트 실패:', error);
      return false;
    }
  }

  // 시트 초기화 (모든 데이터 삭제)
  async clearSheet(sheetTitle) {
    try {
      const sheet = await this.getOrCreateSheet(sheetTitle);
      await sheet.clear();
      
      // 헤더 다시 설정
      await sheet.setHeaderRow([
        '과목코드',
        '과목명', 
        '학습단위',
        '학습제목',
        '토픽ID',
        '토픽명',
        '내용유형',
        '키워드',
        '요약내용',
        '상세설명',
        '청크수',
        '총길이',
        '수정일시'
      ]);
      

      return true;
    } catch (error) {
      console.error('시트 초기화 실패:', error);
      return false;
    }
  }
}

// 싱글톤 인스턴스 생성
const googleSheetsService = new GoogleSheetsService();

// 환경변수나 설정에서 스프레드시트 ID와 서비스 계정 정보 가져오기
export const initializeGoogleSheets = async () => {
  // 실제 사용시에는 환경변수나 별도 설정 파일에서 가져와야 함
  const SPREADSHEET_ID = process.env.REACT_APP_GOOGLE_SPREADSHEET_ID;
  const SERVICE_ACCOUNT_KEY = {
    client_email: process.env.REACT_APP_GOOGLE_CLIENT_EMAIL,
    private_key: process.env.REACT_APP_GOOGLE_PRIVATE_TOKEN?.replace(/\\n/g, '\n')
  };

  if (!SPREADSHEET_ID || !SERVICE_ACCOUNT_KEY.client_email || !SERVICE_ACCOUNT_KEY.private_key) {
    console.warn('Google Sheets 설정이 완료되지 않았습니다. 환경변수를 확인하세요.');
    return false;
  }

  return await googleSheetsService.initialize(SPREADSHEET_ID, SERVICE_ACCOUNT_KEY);
};

export default googleSheetsService;